import React, { useState, useEffect, useCallback } from 'react';

// LIBS
import { debounce } from 'lodash';

// STYLES
import { CircularProgress, Autocomplete, TextField, Chip, Box } from '@mui/material';

// APIS
import { apiListEstablishment } from 'js/library/utils/API/Establishment/apiListEstablishments';
import { findStoreByProximity } from 'js/library/utils/API/Store/findStoreByProximity';
import getEstablishmentInBatch from 'js/library/utils/API/Establishment/getEstablishmentInBatch';
import getPromotionInBatch from 'js/library/utils/API/Store/getPromotionInBatch';

export default function AutocompleteListType({
  type,
  typeCard,
  loading,
  setLoading,
  selectedEstablishment,
  setSelectedEstablishment,
  selectedStore,
  setSelectedStore,
  selectedProduct,
  setSelectedProduct,
  selectedIdEdit,
  groupId,
  index,
  setGroups,
  setInvalidFields,
  isMobile,
  error,
}) {
  const [dataEstablishment, setDataEstablishment] = useState([]);
  const [dataStore, setDataStore] = useState([]);
  const [dataProduct, setDataProduct] = useState([]);

  const [optionsOpen, setOptionsOpen] = useState(false);
  const [textFieldValue, setTextFieldValue] = useState('');

  const isArrayType = ['array_contains', 'array_not_contains'].includes(type);

  useEffect(() => {
    if (isArrayType) {
      setSelectedEstablishment(null);
      setSelectedStore(null);
      setSelectedProduct(null);
    }
  }, [isArrayType]);

  const loadSelectedItem = useCallback(async () => {
    if (!selectedIdEdit) return;

    try {
      setLoading(true);

      // Normaliza para array (caso seja string única ou array)
      const idsArray = Array.isArray(selectedIdEdit) ? selectedIdEdit : [selectedIdEdit];

      // Remove possíveis strings vazias e duplicatas
      const cleanIds = idsArray
        .map((id) => (typeof id === 'string' ? id.split(',') : [])) // Caso venha como string separada por vírgulas
        .flat()
        .filter((id) => id && id.trim() !== '');

      switch (typeCard) {
        case 'establishment_list':
          const establishment = await getEstablishmentInBatch(cleanIds);
          // se não for arrayType, pega o primeiro item
          const estabValue = isArrayType
            ? establishment.response.establishmentList ?? null
            : establishment.response.establishmentList?.[0] ?? null;
          setSelectedEstablishment(estabValue);
          break;

        case 'promotion_list':
          const promotion = await getPromotionInBatch(cleanIds);
          // se não for arrayType, pega o primeiro item
          const promoValue = isArrayType
            ? promotion.response.storeList ?? null
            : promotion.response.storeList?.[0] ?? null;
          setSelectedStore(promoValue);
          break;

        case 'product_list':
          const product = await getPromotionInBatch(cleanIds);
          // se não for arrayType, pega o primeiro item
          const productValue = isArrayType
            ? product.response.storeList ?? null
            : product.response.storeList?.[0] ?? null;
          setSelectedProduct(productValue);
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Erro ao carregar item selecionado:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedIdEdit, typeCard]);

  useEffect(() => {
    if (typeCard === 'promotion_list') {
      setDataEstablishment([]);
      setSelectedEstablishment(null);
    } else if (typeCard === 'establishment_list') {
      setDataStore([]);
      setSelectedStore(null);
    }
    setTextFieldValue('');
  }, [typeCard]);

  useEffect(() => {
    if (selectedIdEdit) {
      loadSelectedItem();
    } else {
      // Reset quando não há ID selecionado
      setTextFieldValue('');
      if (typeCard === 'promotion_list') {
        setSelectedEstablishment(null);
        setDataEstablishment([]);
      } else if (typeCard === 'establishment_list') {
        setSelectedStore(null);
        setDataStore([]);
      }
    }
  }, [selectedIdEdit, typeCard, loadSelectedItem]);

  const labelType = () => {
    switch (typeCard) {
      case 'establishment_list':
        return 'Selecionar estabelecimento';
      case 'promotion_list':
        return 'Selecionar promoção';
      case 'product_list':
        return 'Selecionar produto';
      default:
        break;
    }
  };

  const optionsAutoComplete = () => {
    switch (typeCard) {
      case 'establishment_list':
        return dataEstablishment;
      case 'promotion_list':
        return dataStore;
      case 'product_list':
        return dataProduct;
      default:
        break;
    }
  };

  // dados dos tipos para o autoComplete
  const fetchData = async (query = '', from = 0, size = 10) => {
    const payloadStore = {
      title: query,
      isPublished: true,
      from: from,
      size: size,
    };

    const payloadProduct = {
      title: query,
      isPublished: true,
      from: from,
      size: 5000,
    };

    try {
      let establishmentData;
      let storeData;
      let productData;

      switch (typeCard) {
        case 'establishment_list':
          // estabelecimentos
          establishmentData = await apiListEstablishment(query, from, size);
          setDataEstablishment(establishmentData);
          break;
        case 'promotion_list':
          // promoçoões
          storeData = await findStoreByProximity(payloadStore);
          setDataStore(storeData);
          break;
        case 'product_list':
          // produtos
          productData = await findStoreByProximity(payloadProduct);
          const filteredProducts = productData.filter((item) => item.type === 'product');
          setDataProduct(filteredProducts);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Ocorreu um erro na requisição:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [typeCard]);

  const performFetch = async (query) => {
    setLoading(true);

    // Se o valor digitado não estiver na lista inicial, fazer nova requisição (estabelecimento e promoção)
    if (
      (!dataEstablishment.some(
        (establishment) => establishment.nome.toLowerCase() === query.toLowerCase()
      ) &&
        query.length >= 2) ||
      (!dataStore.some((store) => store.title.toLowerCase() === query.toLowerCase()) &&
        query.length >= 2)
    ) {
      await fetchData(query, 0, 10);
      setLoading(false);
    }
  };

  // Função debounce utilizando lodash
  const debouncedFetch = useCallback(
    debounce(async (query) => {
      await performFetch(query);
    }, 1000),
    [dataEstablishment, dataStore]
  );

  useEffect(() => {
    debouncedFetch(textFieldValue.trim());
  }, [textFieldValue]);

  const handleChange = (event, value) => {
    if (isArrayType) {
      // Garante que value seja um array
      const selectedItems = Array.isArray(value) ? value : null;

      // Atualiza estados locais
      if (typeCard === 'establishment_list') {
        setSelectedEstablishment(selectedItems);
      } else if (typeCard === 'promotion_list') {
        setSelectedStore(selectedItems);
      } else if (typeCard === 'product_list') {
        setSelectedProduct(selectedItems);
      }

      // Atualiza groups com array de IDs
      const ids = selectedItems.map((item) => item.id);
      setGroups((prevGroups) => {
        const updatedGroup = [...prevGroups[groupId]];
        updatedGroup[index] = {
          ...updatedGroup[index],
          value: ids,
        };
        return {
          ...prevGroups,
          [groupId]: updatedGroup,
        };
      });
    } else {
      // Lógica para seleção única
      if (value) {
        const selectedId = value.id;

        // Atualiza os estados locais
        if (typeCard === 'establishment_list') {
          // const filtered = dataEstablishment.filter((item) => item.id === selectedId);
          setSelectedEstablishment(value || null);
        } else if (typeCard === 'promotion_list') {
          // const filtered = dataStore.filter((item) => item.id === selectedId);
          setSelectedStore(value || null);
        } else if (typeCard === 'product_list') {
          // const filtered = dataProduct.filter((item) => item.id === selectedId);
          setSelectedProduct(value || null);
        }

        // Atualiza o groups
        setGroups((prevGroups) => {
          const updatedGroup = [...prevGroups[groupId]];
          updatedGroup[index] = {
            ...updatedGroup[index],
            value: selectedId,
          };
          return {
            ...prevGroups,
            [groupId]: updatedGroup,
          };
        });
      } else {
        // Limpa a seleção
        if (typeCard === 'establishment_list') {
          setSelectedEstablishment(null);
        } else if (typeCard === 'promotion_list') {
          setSelectedStore(null);
        } else if (typeCard === 'product_list') {
          setSelectedProduct(null);
        }

        setGroups((prevGroups) => {
          const updatedGroup = [...prevGroups[groupId]];
          updatedGroup[index] = {
            ...updatedGroup[index],
            value: '',
          };
          return {
            ...prevGroups,
            [groupId]: updatedGroup,
          };
        });
      }
    }

    // Limpa o erro para este campo
    setInvalidFields((prev) => {
      const newErrors = { ...prev };
      if (newErrors[groupId]?.[index] !== undefined) {
        delete newErrors[groupId][index];
        if (Object.keys(newErrors[groupId]).length === 0) {
          delete newErrors[groupId];
        }
      }
      return newErrors;
    });
  };

  return (
    <Autocomplete
      disablePortal
      id="combo-box-demo"
      multiple={isArrayType}
      options={optionsAutoComplete()}
      getOptionLabel={(option) => {
        if (!option) return '';
        return option?.nome || option?.title || '';
      }}
      value={
        isArrayType
          ? typeCard === 'establishment_list'
            ? Array.isArray(selectedEstablishment)
              ? selectedEstablishment
              : []
            : typeCard === 'promotion_list'
            ? Array.isArray(selectedStore)
              ? selectedStore
              : []
            : typeCard === 'product_list'
            ? Array.isArray(selectedProduct)
              ? selectedProduct
              : []
            : []
          : typeCard === 'establishment_list'
          ? selectedEstablishment || null
          : typeCard === 'promotion_list'
          ? selectedStore || null
          : typeCard === 'product_list'
          ? selectedProduct || null
          : null
      }
      fullWidth
      blurOnSelect
      noOptionsText={loading ? <CircularProgress size={15} /> : 'Nenhum resultado encontrado...'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={labelType()}
          onClick={() => setOptionsOpen(true)}
          onFocus={() => setOptionsOpen(true)}
          onBlur={() => setOptionsOpen(false)}
          error={error}
          sx={{
            marginTop: isArrayType && isMobile ? '10px' : undefined,
            '& .MuiOutlinedInput-root': {
              padding: isArrayType ? '6px' : '0px',
              minHeight: '40px',
              height: 'auto',
              alignItems: 'flex-start',
            },
            '& .MuiInputLabel-root': {
              transform: !isArrayType ? 'translate(14px, 9px) scale(1)' : undefined,
              top: isArrayType ? '-9px' : undefined,
              '&.Mui-focused': {
                transform: 'translate(14px, -6px) scale(0.75)',
              },
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -6px) scale(0.75)',
              },
            },
            '& .MuiInputBase-input': {
              padding: isArrayType ? '0 4px !important' : '8px 14px !important',
            },
            width: '100%',
          }}
        />
      )}
      renderTags={(value, getTagProps) => (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            maxWidth: '100%',
            padding: '4px',
          }}
        >
          {value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={option.nome || option.title}
              size="small"
              sx={{
                maxWidth: '100%',
                margin: '2px',
                '& .MuiChip-label': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: '0 8px',
                },
              }}
            />
          ))}
        </Box>
      )}
      ListboxProps={{ style: { maxHeight: '200px' } }}
      open={optionsOpen}
      onClose={() => setOptionsOpen(false)}
      onChange={handleChange}
      onInputChange={(event, newInputValue) => {
        setTextFieldValue(newInputValue);
        if (newInputValue === '') {
          fetchData('', 0, 10);
        }
      }}
      sx={{
        '& .MuiAutocomplete-root': {
          width: '100%',
        },
        '& .MuiAutocomplete-inputRoot': {
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          padding: isArrayType ? '4px' : '0px',
          gap: '6px',
        },
        '& .MuiAutocomplete-endAdornment': {
          top: '50%',
          transform: 'translateY(-50%)',
        },
        width: '100%',
      }}
    />
  );
}
